/**
 * AdminEmptyState - Full-width empty state component for admin interfaces
 *
 * SESSION_315: Sprint 026C - Fix admin empty state display width
 *
 * Provides a consistent, desktop-friendly empty state that:
 * - Takes full container width (uses Paper, not Center)
 * - Centers content visually while maintaining layout structure
 * - Uses refined, professional styling appropriate for admin UX
 *
 * Design: Refined utilitarian - subtle dashed border and soft background
 * indicate "awaiting content" while maintaining professional appearance.
 */
import React from 'react';
import { Paper, Stack, Text, Box } from '@mantine/core';
import { IconInbox } from '@tabler/icons-react';

/**
 * Props for AdminEmptyState component
 */
export interface AdminEmptyStateProps {
  /** Custom icon to display (default: IconInbox) */
  icon?: React.ReactNode;
  /** Primary message to display */
  title: string;
  /** Optional secondary description text */
  description?: string;
  /** Optional minimum height (default: 200px) */
  minHeight?: number | string;
}

/**
 * AdminEmptyState Component
 *
 * A full-width empty state indicator for admin review screens.
 * Uses Paper for container width stability, with centered content inside.
 */
export const AdminEmptyState: React.FC<AdminEmptyStateProps> = ({
  icon,
  title,
  description,
  minHeight = 200
}): React.ReactElement => {
  return (
    <Paper
      p="xl"
      radius="md"
      withBorder
      style={{
        borderStyle: 'dashed',
        borderColor: 'var(--mantine-color-gray-4)',
        backgroundColor: 'var(--mantine-color-gray-0)',
        minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Stack gap="md" align="center">
        <Box
          style={{
            color: 'var(--mantine-color-gray-5)',
            opacity: 0.8
          }}
        >
          {icon ?? <IconInbox size={52} stroke={1.2} />}
        </Box>
        <Stack gap={4} align="center">
          <Text
            fw={500}
            size="md"
            c="dimmed"
            style={{ letterSpacing: '-0.01em' }}
          >
            {title}
          </Text>
          {description && (
            <Text size="sm" c="dimmed" style={{ opacity: 0.7 }}>
              {description}
            </Text>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};
